import {
  Cta,
  Header,
  Input,
  Main
} from "@website/ui-old/datepicker/components";
import styles from "@website/ui-old/datepicker/datepicker.module.scss";
import { DatepickerPropertiesType } from "@website/ui-old/datepicker/datepicker.types";
import {
  useCalendarLocale,
  usePopover,
  useScroll,
  useShowDate
} from "@website/ui-old/datepicker/hooks";
import { PopoverFull } from "@website/ui-old/popover-full";
import { holidaysList } from "@website/ui/datepicker/components/main/holidays";
import { FC, memo, useCallback, useState } from "react";

const Datepicker: FC<DatepickerPropertiesType> = (properties) => {
  const [isStartFocused, setIsStartFocused] = useState(false);
  const [isEndFocused, setIsEndFocused] = useState(false);
  const {
    disabled,
    disabledDateMode,
    error,
    fullWidth,
    helperText,
    mode,
    setMode,
    onChange,
    fromTitle,
    toTitle,
    value,
    changeableMode,
    calendarData,
    secondaryMode,
    holidays,
    queryKey,
    prices,
    onFocus,
    hideIcons,
    hideBorders,
    hasTitleLabel
  } = properties;

  const { onOpenPopover, onClosePopover, popoverProperties } = usePopover(
    queryKey,
    onFocus
  );
  const { scrollReference, setScrollReference, scrolled, scrollToTop } =
    useScroll();

  const {
    calendarLocale,
    changeCalendarLocaleToGregorian,
    changeCalendarLocaleToPersian
  } = useCalendarLocale(properties);

  const {
    onNextMonthClick,
    onPreviousMonthClick,
    onShowTodayClick,
    showDates,
    increaseShowDatesNumber,
    nextMonthDisabled,
    previousMonthDisabled
  } = useShowDate(calendarLocale, scrollToTop);

  const onAccept = useCallback(() => {
    onClosePopover();
  }, [onClosePopover]);

  const onReject = useCallback(() => {
    onChange([], calendarLocale);
  }, [calendarLocale, onChange]);

  const onFocusStart = useCallback(() => {
    setIsEndFocused(false);
    setIsStartFocused(true);
  }, []);

  const onFocusEnd = useCallback(() => {
    setIsStartFocused(false);
    setIsEndFocused(true);
  }, []);

  const onBlurInputs = useCallback(() => {
    setIsEndFocused(false);
    setIsStartFocused(false);
  }, []);

  return (
    <div className={`${styles["datepicker"]} ${styles["rtl" || "ltr"]}`}>
      <Input
        calendarLocale={calendarLocale}
        changeableMode={changeableMode}
        disabled={disabled}
        error={error}
        fromTitle={fromTitle}
        fullWidth={fullWidth}
        hasTitleLabel={hasTitleLabel}
        helperText={helperText}
        hideBorders={hideBorders}
        hideIcons={hideIcons}
        mode={mode}
        onFocusEnd={onFocusEnd}
        onFocusStart={onFocusStart}
        onOpenPopover={onOpenPopover}
        secondaryMode={secondaryMode}
        setMode={setMode}
        toTitle={toTitle}
        value={value}
      />
      <PopoverFull {...{ ...popoverProperties, onBlurInputs }}>
        <div className={styles["popover"]}>
          <Header
            calendarLocale={calendarLocale}
            changeCalendarLocale={
              calendarLocale === "persian"
                ? changeCalendarLocaleToGregorian
                : changeCalendarLocaleToPersian
            }
            inputProperties={{
              fromTitle,
              toTitle,
              mode,
              setMode,
              value,
              calendarLocale: calendarLocale,
              changeableMode,
              secondaryMode,
              onFocusStart,
              onFocusEnd
            }}
            nextMonthDisabled={nextMonthDisabled}
            onClose={onClosePopover}
            previousMonthDisabled={previousMonthDisabled}
            scrolled={scrolled}
            showDates={showDates}
            showNextMonth={onNextMonthClick}
            showPreviousMonth={onPreviousMonthClick}
            showToday={onShowTodayClick}
          />
          <Main
            calendarData={calendarData}
            calendarLocale={calendarLocale}
            disabledDateMode={disabledDateMode}
            holidays={holidays || holidaysList}
            increaseShowDatesNumber={increaseShowDatesNumber}
            isEndFocused={isEndFocused}
            isStartFocused={isStartFocused}
            mode={mode}
            onBlurInputs={onBlurInputs}
            onChange={onChange}
            onFocusEnd={onFocusEnd}
            prices={prices}
            scrollReference={scrollReference}
            setScrollReference={setScrollReference}
            showDates={showDates}
            value={value}
          />
          <div className="flex items-center justify-between">
            <div className="text-success flex items-center gap-1 px-6 py-4 text-xs">
              {prices && Array.isArray(prices) && prices.length > 0 && (
                <>
                  <div className="bg-transparent-success size-3 rounded-full" />
                  ارزران‌ترین قیمت
                </>
              )}
            </div>
            <Cta
              accept={onAccept}
              mode={mode}
              reject={onReject}
              value={value}
            />
          </div>{" "}
        </div>
      </PopoverFull>
    </div>
  );
};

const DatepickerMemoized = memo(Datepicker);
/**
 * @deprecated please use @website/ui instead
 */
export { DatepickerMemoized /**
 * @deprecated please use @website/ui instead
 */ as Datepicker };
